import { EqlFieldCommonMetadataSourceListTooltip } from '@modules/common/components/EqlFieldCommonMetadataInfo';
import { TypographyWithEllipsis } from '@modules/common/components/TypographyWithEllipsis';
import { useIsUnifiedFieldsEnabled } from '@modules/common/hooks';
import {
  QueryStringMetadataOptionFromFilter,
  QueryStringNonMetadataOptionFromFilter,
} from '@modules/common/types';
import { isCommonMetadataField } from '@modules/common/utils';
import { InfoOutlined } from '@mui/icons-material';
import { Stack, Tooltip, Typography } from '@mui/material';
import { OperationType } from 'v2/core/services/queryBuilder/whereClause';
import { CommonFieldDetailsTooltipContent } from '../CommonFieldDetailsTooltipContent';
import { EntitySentimentFilterPreview } from './EntitySentimentFilterPreview';

interface SimpleFilterPreviewProps {
  fieldDisplayName: string;
  filterData:
    | QueryStringNonMetadataOptionFromFilter
    | QueryStringMetadataOptionFromFilter;
  conjunction?: OperationType;
  shouldHideTooltips?: boolean;
}

export const SimpleFilterPreview = ({
  fieldDisplayName,
  filterData,
  conjunction,
  shouldHideTooltips = false,
}: SimpleFilterPreviewProps) => {
  const { fieldName, values, operator, eqlField } = filterData;
  const isUnifiedFieldsEnabled = useIsUnifiedFieldsEnabled();

  // is Entity Sentiment which have a syntax of 'uuid:SentimentName'
  if (fieldName === 'Entity' && values[0].includes(':')) {
    return (
      <EntitySentimentFilterPreview values={values} conjunction={conjunction} />
    );
  }

  const renderInfo = () => {
    if (!eqlField || shouldHideTooltips) {
      return null;
    }

    if (isUnifiedFieldsEnabled && isCommonMetadataField(eqlField)) {
      return (
        <Tooltip title={<CommonFieldDetailsTooltipContent field={eqlField} />}>
          <InfoOutlined
            fontSize="small"
            sx={{
              color: 'text.secondary',
              fontSize: '1rem',
            }}
          />
        </Tooltip>
      );
    }

    return (
      <EqlFieldCommonMetadataSourceListTooltip eqlField={eqlField}>
        <InfoOutlined
          fontSize="small"
          sx={{
            color: 'text.secondary',
            fontSize: '1rem',
          }}
        />
      </EqlFieldCommonMetadataSourceListTooltip>
    );
  };

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" color="text.secondary">
        {conjunction}
      </Typography>
      <Stack sx={{ pb: 1 }}>
        <Stack direction="row" spacing={1}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="caption" color="text.secondary">
              {fieldDisplayName}
            </Typography>
            {renderInfo()}
          </Stack>
          <Typography variant="caption" color="text.secondary">
            ({operator})
          </Typography>
        </Stack>
        <TypographyWithEllipsis variant="body2" color="primary">
          {values.join(', ')}
        </TypographyWithEllipsis>
      </Stack>
    </Stack>
  );
};
